import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FieldGroups } from "./FieldGroups";
import EntityCombobox from "./EntityCombobox";
import { TESTIDS } from "@/lib/testIds";

/**
 * ManualEntry — progressive one-question-at-a-time entry surface for
 * users without a booking PDF.
 *
 * LAW-001 · Enter Once → Use Everywhere (PO directive 2026-08-07):
 *   Cruise Line and Ship are canonical reference entities. Both are
 *   selected via searchable comboboxes bound to `/api/ckb/cruise-lines`
 *   and `/api/ckb/ships?line=<id>`. No arbitrary text is ever accepted;
 *   if no match exists, a "Create new…" action provisions a canonical
 *   record before the wizard continues.
 *
 * Required steps:
 *   1 · Cruise line (combobox)
 *   2 · Ship        (combobox — filtered by selected cruise line)
 *   3 · Sail + return dates
 *   4 · Nickname
 */
export default function ManualEntry({
  fields,
  editField,
  openGroups,
  setOpenGroups,
  onSubmit,
  onBack,
  submitting,
  lineId,
  setLineId,
  shipId,
  setShipId,
}) {
  const [step, setStep] = useState(0);
  const [showMore, setShowMore] = useState(false);

  const [lineOptions, setLineOptions] = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);

  const [shipOptions, setShipOptions] = useState([]);
  const [shipsLoading, setShipsLoading] = useState(false);

  const line = (fields.cruise_line || "").trim();
  const ship = (fields.ship || "").trim();
  const sail = fields.sailing_date;
  const ret = fields.return_date;
  const name = (fields.name || "").trim();

  // ---- Suggested-name state (PO bug fix 2026-08-07) --------------------
  // The nickname is auto-suggested from the current wizard state
  // (Ship → Cruise Line fallback · Departure Month/Year) and MUST
  // regenerate whenever ship or sailing date changes. As soon as the
  // user hand-edits the field, we lock auto-suggestion for the rest
  // of the wizard session so we never overwrite their choice.
  //
  // `nameLocked` starts false on every fresh mount, so a stale name
  // from a previously created cruise cannot survive into a new session.
  const [nameLocked, setNameLocked] = useState(false);
  const lastAutoSuggestRef = useRef("");
  // On mount, if fields.name carries a value that matches the auto-
  // suggest pattern (`Ship · MMM YYYY`) treat it as auto-suggested,
  // not user-edited. If it looks user-original, lock immediately.
  useEffect(() => {
    if (!name) return;
    const looksAutoSuggested = /·\s+[A-Z][a-z]{2}\s+\d{4}$/.test(name);
    if (!looksAutoSuggested) setNameLocked(true);
    // Only run this check on the first paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Load cruise-line catalog once. -----------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLinesLoading(true);
      try {
        const res = await api.get("/api/ckb/cruise-lines");
        if (cancelled) return;
        // FIRST-CRUISE onboarding (2026-02-20 · PO): the picker must be
        // strictly alphabetical by cruise-line name. Category (Contemporary
        // / Premium / Luxury / Expedition …) remains available as the
        // combobox sublabel so it's discoverable, but MUST NOT drive the
        // primary sort order — an alphabetical list is the fastest scan
        // for someone who already knows the line they booked.
        const items = (res.data?.cruise_lines || [])
          .map((c) => ({
            id: c.id,
            name: c.name,
            sublabel: c.private ? "Only visible to you" : (c.category ? c.category.replace(/_/g, " ") : undefined),
            aliases: c.aliases,
          }))
          .sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
          );
        setLineOptions(items);
      } catch (e) {
        console.error("Failed to load cruise lines", e);
        toast.error("Could not load the cruise-line library.");
      } finally {
        if (!cancelled) setLinesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- Load ships filtered by selected cruise line. ---------------------
  useEffect(() => {
    let cancelled = false;
    if (!lineId) { setShipOptions([]); return; }
    (async () => {
      setShipsLoading(true);
      try {
        const res = await api.get(`/api/ckb/ships?line=${encodeURIComponent(lineId)}`);
        if (cancelled) return;
        const items = (res.data?.ships || [])
          .map((s) => ({
            id: s.id,
            name: s.name,
            sublabel: [s.ship_class, s.year_built].filter(Boolean).join(" · ") || undefined,
          }))
          .sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
          );
        setShipOptions(items);
      } catch (e) {
        console.error("Failed to load ships", e);
        toast.error("Could not load ships for that cruise line.");
      } finally {
        if (!cancelled) setShipsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lineId]);

  // ---- Create-new handlers (LAW-001). -----------------------------------
  const createCruiseLine = useCallback(async (nameToCreate) => {
    try {
      const res = await api.post("/api/ckb/cruise-lines", { name: nameToCreate });
      const cl = res.data?.cruise_line;
      if (!cl?.id) throw new Error("Create response missing id");
      // Refresh the catalog so the new entry participates in search
      // AND stays alphabetically sorted with the rest of the library.
      setLineOptions((prev) => {
        if (prev.some((p) => p.id === cl.id)) return prev;
        const next = [...prev, { id: cl.id, name: cl.name, sublabel: cl.private ? "Only visible to you" : cl.category }];
        next.sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
        );
        return next;
      });
      toast.success(cl.private
        ? `Added ${cl.name} to your cruise lines (only visible to you).`
        : `Added ${cl.name} to the cruise-line library.`);
      return cl;
    } catch (e) {
      toast.error(formatApiError(e) || "Could not create cruise line.");
      return null;
    }
  }, []);

  const onLineSelected = useCallback((id, entity) => {
    setLineId(id);
    editField("cruise_line", entity?.name || "");
    // Selecting a new line clears the ship — a Carnival ship cannot
    // remain selected under a Royal Caribbean line.
    if (shipId) {
      setShipId(null);
      editField("ship", "");
    }
  }, [editField, setLineId, setShipId, shipId]);

  const onShipSelected = useCallback((id, entity) => {
    setShipId(id);
    editField("ship", entity?.name || "");
  }, [editField, setShipId]);

  // Auto-suggest a nickname from Ship (preferred) → Cruise Line fallback
  // + Departure Month/Year. Regenerates on every ship/line/date change
  // until the user hand-edits the field (`nameLocked = true`).
  useEffect(() => {
    if (nameLocked) return;
    if (!sail) return;
    const anchor = ship || line;
    if (!anchor) return;
    try {
      const m = /^(\d{4})-(\d{2})/.exec(sail);
      if (!m) return;
      const monthYear = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, 1)
        .toLocaleDateString(undefined, { month: "short", year: "numeric" });
      const suggested = `${anchor} · ${monthYear}`;
      if (suggested === name) return; // already current
      lastAutoSuggestRef.current = suggested;
      editField("name", suggested);
    } catch { /* ignore */ }
  }, [ship, line, sail, name, nameLocked, editField]);

  // Detect a user hand-edit so subsequent ship/date changes never
  // clobber it. Compares against the last programmatic suggestion; any
  // divergence beyond that is treated as intentional.
  const onNameChanged = useCallback((next) => {
    if (!nameLocked && next && next !== lastAutoSuggestRef.current) {
      setNameLocked(true);
    }
    editField("name", next);
  }, [nameLocked, editField]);

  const STEPS = [
    {
      key: "line",
      title: "Which cruise line?",
      hint: "Start typing — we'll match against the Cruise Line Library.",
      valid: !!lineId,
      body: (
        <EntityCombobox
          value={lineId}
          valueLabel={line}
          items={lineOptions}
          loading={linesLoading}
          placeholder="Search cruise lines…"
          emptyLabel="No cruise lines match that search."
          createLabel="Create new cruise line"
          onCreate={createCruiseLine}
          onChange={onLineSelected}
          testId={TESTIDS.createCruiseManualLine}
          testIdOption={TESTIDS.createCruiseManualLineOption}
          testIdCreate={TESTIDS.createCruiseManualLineCreate}
          autoFocus
        />
      ),
    },
    {
      key: "ship",
      title: "Which ship?",
      hint: lineId
        ? "Choose from ships that sail under this cruise line."
        : "Pick a cruise line first.",
      valid: !!shipId,
      body: (
        <EntityCombobox
          value={shipId}
          valueLabel={ship}
          items={shipOptions}
          loading={shipsLoading}
          disabled={!lineId}
          placeholder={lineId ? "Search ships…" : "Select a cruise line first"}
          emptyLabel={
            lineId
              ? "No ships found for this cruise line."
              : "Select a cruise line first."
          }
          onChange={onShipSelected}
          testId={TESTIDS.createCruiseManualShip}
          testIdOption={TESTIDS.createCruiseManualShipOption}
          autoFocus
        />
      ),
    },
    {
      key: "dates",
      title: "When do you sail?",
      hint: "Embark and return — we'll fill in the rest.",
      valid: !!sail && !!ret,
      body: (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Embark</div>
            <input
              type="date"
              autoFocus
              value={fields.sailing_date || ""}
              onChange={(e) => editField("sailing_date", e.target.value)}
              className="w-full text-[15px] px-4 py-3 border border-slate-200 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 focus:outline-none"
              data-testid={TESTIDS.createCruiseManualSail}
            />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Return</div>
            <input
              type="date"
              value={fields.return_date || ""}
              onChange={(e) => editField("return_date", e.target.value)}
              className="w-full text-[15px] px-4 py-3 border border-slate-200 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 focus:outline-none"
              data-testid={TESTIDS.createCruiseManualReturn}
            />
          </div>
        </div>
      ),
    },
    {
      key: "name",
      title: "How should we refer to this trip?",
      hint: "Something evocative — you'll see it everywhere.",
      valid: !!name,
      body: (
        <input
          autoFocus
          value={fields.name || ""}
          onChange={(e) => onNameChanged(e.target.value)}
          placeholder={ship && sail ? "e.g. Symphony · Southern Caribbean" : "Give this trip a name"}
          className="w-full text-[16px] px-4 py-3 border border-slate-200 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 focus:outline-none"
          data-testid={TESTIDS.createCruiseManualName}
        />
      ),
    },
  ];

  const cur = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const allValid = STEPS.every((s) => s.valid);

  return (
    <div className="px-10 py-9" data-testid={TESTIDS.createCruiseManual}>
      {/* Guided-setup layout · reserve room on the right for future
          contextual guidance (why we're asking · what happens next ·
          tips). Content column is capped so a right rail can slot in
          without a re-layout. */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_18rem] gap-10">
        <div className="min-w-0 space-y-8">
          {/* Progress — explicit "Step X of N" + strong bar-segment
              track. This is the primary "where am I" cue in the
              wizard; the header carries the current stage. */}
          <div>
            <div className="flex items-baseline justify-between mb-2.5">
              <div className="text-[11.5px] font-black tracking-[0.20em] uppercase text-amber-700" data-testid="cc-create-cruise-manual-step-label">
                Step {step + 1} of {STEPS.length}
              </div>
              <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                {cur.key === "line" ? "Cruise Line"
                  : cur.key === "ship" ? "Ship"
                  : cur.key === "dates" ? "Sailing Dates"
                  : "Nickname"}
              </div>
            </div>
            <div className="flex items-center gap-1.5" aria-label="progress">
              {STEPS.map((s, i) => (
                <span
                  key={s.key}
                  className={`h-2 rounded-full transition-all flex-1 ${
                    i < step ? "bg-emerald-500"
                    : i === step ? "bg-amber-500"
                    : "bg-slate-200"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Step heading — larger register, more air between title
              and the input control. */}
          <div>
            <h3 className="font-display text-[28px] font-bold text-slate-900 leading-tight" data-testid={TESTIDS.createCruiseManualStepTitle}>
              {cur.title}
            </h3>
            <div className="text-[14px] text-slate-500 mt-2 max-w-xl leading-relaxed">{cur.hint}</div>
          </div>

          <div>{cur.body}</div>

          {isLast && (
            <div className="pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowMore((v) => !v)}
                className="text-[12.5px] font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                data-testid={TESTIDS.createCruiseManualMoreToggle}
              >
                {showMore ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                Add more details (optional — booking number, cabin, agent, costs)
              </button>
              {showMore && (
                <div className="mt-4">
                  <FieldGroups
                    fields={fields}
                    editField={editField}
                    openGroups={openGroups}
                    setOpenGroups={setOpenGroups}
                    groupFilter={["reference", "cabin", "travelers", "money", "extras"]}
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-4 border-t border-slate-200 mt-2">
            <button
              type="button"
              onClick={step === 0 ? onBack : () => setStep((s) => Math.max(0, s - 1))}
              className="text-[13px] font-semibold text-slate-500 hover:text-slate-700 px-3 py-2.5"
              data-testid={TESTIDS.createCruiseManualBack}
            >
              {step === 0 ? "Back" : "Previous"}
            </button>
            {isLast ? (
              <button
                type="button"
                onClick={onSubmit}
                disabled={!allValid || submitting}
                className="inline-flex items-center gap-2 px-7 py-3 text-[14px] font-semibold rounded-full bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                data-testid={TESTIDS.createCruiseManualCreate}
              >
                {submitting ? "Creating…" : "Create cruise"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                disabled={!cur.valid}
                className="inline-flex items-center gap-2 px-7 py-3 text-[14px] font-semibold rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={TESTIDS.createCruiseManualNext}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Right rail — reserved for future contextual guidance
            (why we ask, what Cruise Command does next, tips). Kept
            silent for now per PO directive; the space communicates
            "guided experience" even while empty. Hidden on tablet
            and below to preserve the mobile flow. */}
        <aside
          className="hidden lg:block"
          aria-hidden="true"
          data-testid="cc-create-cruise-manual-context-rail"
        />
      </div>
    </div>
  );
}
